import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useOutbox } from '@sportschool/shared-logic';

const UPDATE_TOAST_ID = 'pwa-update';

/** How often an open app checks for a new version (the page is rarely reloaded, e.g. in the mobile app's WebView). */
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** A new version found this soon after the app came back to the foreground is applied at once: nothing was started yet. */
const RESUME_APPLY_WINDOW_MS = 15 * 1000;

/** Dispatched by the mobile app (apps/mobile/App.tsx) when it returns to the foreground. */
const NATIVE_RESUME_EVENT = 'native-app-resume';

interface PwaUpdatePromptProps {
  /** Return false to postpone applying the update (the page reload). */
  canReload?: () => boolean;
}

/**
 * Registers the service worker and keeps the app on the latest version.
 * - Checks for a new version every few minutes, when the app returns to the foreground and when it gets back online.
 *   The browser checks only on page loads, and the mobile app's WebView keeps one page alive for days.
 * - Applies a new version (reloads) on its own while the app is in the background or right after it returns to the
 *   foreground; while the user works it shows a toast with an update action instead.
 * - Never reloads while the offline outbox is being sent (the queued entries themselves are kept in IndexedDB).
 */
export function PwaUpdatePrompt({ canReload }: PwaUpdatePromptProps) {
  const { t } = useTranslation();
  const { flushing } = useOutbox();
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);
  const resumedAtRef = useRef(0);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration;
    },
  });

  // Latest values for the event listeners below.
  const stateRef = useRef({ needRefresh, flushing, canReload, updateServiceWorker });
  useEffect(() => {
    stateRef.current = { needRefresh, flushing, canReload, updateServiceWorker };
  }, [needRefresh, flushing, canReload, updateServiceWorker]);

  /** Activates the waiting version and reloads; false when reloading has to wait. */
  const applyRef = useRef(() => {
    const state = stateRef.current;
    if (!state.needRefresh || state.flushing || (state.canReload && !state.canReload())) return false;
    toast.dismiss(UPDATE_TOAST_ID);
    void state.updateServiceWorker(true);
    return true;
  });

  useEffect(() => {
    const check = () => {
      if (navigator.onLine) void registrationRef.current?.update();
    };
    const onResume = () => {
      resumedAtRef.current = Date.now();
      if (!applyRef.current()) check();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onResume();
      else applyRef.current();
    };

    const interval = setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener(NATIVE_RESUME_EVENT, onResume);
    window.addEventListener('online', check);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener(NATIVE_RESUME_EVENT, onResume);
      window.removeEventListener('online', check);
    };
  }, []);

  useEffect(() => {
    if (!needRefresh) return;
    const justResumed = Date.now() - resumedAtRef.current < RESUME_APPLY_WINDOW_MS;
    if ((document.visibilityState === 'hidden' || justResumed) && applyRef.current()) return;

    toast(t('pwa.updateAvailable'), {
      id: UPDATE_TOAST_ID,
      duration: Infinity,
      action: {
        label: t('pwa.updateAction'),
        onClick: () => {
          applyRef.current();
        },
      },
    });
  }, [needRefresh, t]);

  return null;
}
